/**
 * Diálogos com a cara do app no lugar do `window.confirm`: `askConfirm` (sim/não) e `askChoice`
 * (uma opção entre várias, como o ActionSheet de escopo do celular). Devolvem Promise; o
 * `<ConfirmHost />` montado uma vez no App desenha. Sem host (teste, render fora do App) cai no
 * confirm nativo.
 */
import { useEffect, useState } from 'react';
import type { ConfirmOptions } from '../viewer/lib/editing';

export type { ConfirmOptions };

export interface ChoiceOption { id: string; label: string; description?: string; danger?: boolean }
export interface ChoiceOptions { title: string; text?: string; options: ChoiceOption[]; cancelLabel?: string }

type Pending =
  | { kind: 'confirm'; o: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'choice'; o: ChoiceOptions; resolve: (v: string | null) => void };

let host: ((p: Omit<Pending, 'resolve'> & { resolve: (v: never) => void }) => void) | null = null;

export function askConfirm(o: ConfirmOptions): Promise<boolean> {
  if (!host) return Promise.resolve(window.confirm(o.text ? `${o.title}\n\n${o.text}` : o.title));
  return new Promise((resolve) => host!({ kind: 'confirm', o, resolve: resolve as (v: never) => void }));
}

export function askChoice(o: ChoiceOptions): Promise<string | null> {
  if (!host) {
    const idx = window.prompt(`${o.title}\n${o.text ?? ''}\n${o.options.map((x, i) => `${i + 1}. ${x.label}`).join('\n')}`);
    const n = Number(idx);
    return Promise.resolve(n >= 1 && n <= o.options.length ? o.options[n - 1].id : null);
  }
  return new Promise((resolve) => host!({ kind: 'choice', o, resolve: resolve as (v: never) => void }));
}

export function ConfirmHost() {
  const [state, setState] = useState<Pending | null>(null);
  useEffect(() => {
    host = (p) => setState((cur) => {
      if (cur?.kind === 'confirm') cur.resolve(false); else if (cur?.kind === 'choice') cur.resolve(null);
      return p as Pending;
    });
    return () => { host = null; };
  }, []);
  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); cancel(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });
  if (!state) return null;
  const cancel = () => { if (state.kind === 'confirm') state.resolve(false); else state.resolve(null); setState(null); };

  if (state.kind === 'confirm') {
    const { o } = state;
    const done = (v: boolean) => { state.resolve(v); setState(null); };
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 p-4" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" data-testid="confirm">
        <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-lg">
          <h2 id="confirm-title" className="text-[17px] font-semibold text-ink">{o.title}</h2>
          {o.text && <p className="mt-2 text-[14px] leading-relaxed text-mute">{o.text}</p>}
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" autoFocus onClick={() => done(false)} className="rounded-xl px-4 py-2 text-[14px] font-medium text-ink-soft hover:bg-paper-deep" data-testid="confirm-cancel">{o.cancelLabel ?? 'Cancelar'}</button>
            <button type="button" onClick={() => done(true)} className={`rounded-xl px-4 py-2 text-[14px] font-semibold text-white ${o.danger ? 'bg-neg hover:opacity-90' : 'bg-teal hover:bg-teal-dark'}`} data-testid="confirm-ok">{o.confirmLabel ?? 'OK'}</button>
          </div>
        </div>
      </div>
    );
  }

  const { o } = state;
  const pick = (id: string) => { state.resolve(id); setState(null); };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 p-4" role="dialog" aria-modal="true" aria-labelledby="choice-title" data-testid="choice">
      <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-lg">
        <h2 id="choice-title" className="text-[17px] font-semibold text-ink">{o.title}</h2>
        {o.text && <p className="mt-2 text-[14px] leading-relaxed text-mute">{o.text}</p>}
        <div className="mt-5 flex flex-col gap-2">
          {o.options.map((x, i) => (
            <button key={x.id} type="button" autoFocus={i === 0} onClick={() => pick(x.id)} data-testid={`choice-${x.id}`}
              className={`rounded-xl border px-4 py-3 text-left transition hover:border-teal ${x.danger ? 'border-rule hover:border-neg' : 'border-rule'}`}>
              <span className={`block text-[14px] font-semibold ${x.danger ? 'text-neg' : 'text-ink'}`}>{x.label}</span>
              {x.description && <span className="mt-0.5 block text-[13px] text-mute">{x.description}</span>}
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={cancel} className="rounded-xl px-4 py-2 text-[14px] font-medium text-ink-soft hover:bg-paper-deep" data-testid="choice-cancel">{o.cancelLabel ?? 'Cancelar'}</button>
        </div>
      </div>
    </div>
  );
}
